import React, { useEffect, useState } from 'react'
import _ from 'lodash'
import { length } from 'redux-form-validators'
import { SubmissionError } from 'redux-form'
import { HistoryUtil } from '@vzmi/component_ec_nodejs_util/dist/lib'
import { getFeatureToggle } from '@vzmi/component_ec_nodejs_util/dist/lib/FeatureToggleUtil'
// import moment from 'moment' // APACSMDESK-45216 temp
import styles from './QueryForm.css'
import { FormRow, RFSelectInputField, InputCountField, FormError, TextareaField } from '../../../Common/Form'
import { Field } from '../../../Common/forms'
import { Button, ProposalBlock } from '@vzmi/shp_shared_compo'
import { resHandler } from '../../../../../lib/errorHelper'
import { pruneEmpty, promisifySubmitResult } from '../../../../lib/FormHelper'
import { showUniqNumberId } from '../../../../lib/formatter'
import { withSilentPageContext } from '../../../../context/silentPageContext'
// import { getUuid, delay } from '../../../../../lib/renderUtil' // APACSMDESK-45216 temp
import { idsParser } from '@vzmi/component_ec_nodejs_util/dist/lib/parserUtil'
import { idsFormatter } from '../../../Proposal/Common/ProposalForm/Shared/formatters'
import { getContactWindow } from '../../../Proposal/Shared'
import { FetchDataUtil } from '@vzmi/component_ec_nodejs_util'
import { safeAwait } from '@vzmi/component_ec_nodejs_util/dist/lib/FetchDataUtil'

const QUERY_FORM_KEYS = [
  'subStationId',
  'idType',
  'id',
  'name',
  'partNo'
]

const ID_TYPE = {
  LISTING: {
    label: '賣場編號',
    value: 'listing'
  },
  PRODUCT: {
    label: '商品編號',
    value: 'product'
  }
}

// const CACHE_EXPIRE_TIME = [5, 'm'] // APACSMDESK-45216 temp

/**
 * 將 formValues 轉成 GET endpoint 需要的格式，
 * 主要是把 id 轉成 string
 * @param {Object} formValues
 * @returns {Object}
 */
const getFormValuesForQuerying = formValues => {
  // query string 只保留預期的欄位
  formValues = _.pick(pruneEmpty(formValues), QUERY_FORM_KEYS)

  const {
    id = [],
    ...otherValues
  } = formValues

  const idsString = id.join(',') || null

  return _.merge(null, otherValues,
    _.isNull(idsString)
      ? {}
      : { id: idsString }
  )
}

/**
 * 將 formValues 轉成 redux-form load 需要的格式，
 * 主要是 id 要從 string 轉成 Array
 * @param {Object} formValues
 * @returns {Object}
 */
const getFormValueForLoading = formValues => {
  const id = _.get(formValues, 'id', null)
  if (!_.isNull(id)) {
    formValues.id = showUniqNumberId(id.split(','))
  }
  return formValues
}

/**
 * 取得 url 中的 formValues
 */
const getUrlFormValues = props => {
  const queryStringParams = pruneEmpty(HistoryUtil.retrieveQueryParams(props))
  const formValuesFromURL = _.pick(queryStringParams, QUERY_FORM_KEYS)
  const formattedFormValues = getFormValueForLoading(formValuesFromURL)

  return formattedFormValues
}

const lengthMax = max => value => length({ max, msg: '字數超過上限' })(value)

export const idMax = max => collect => collect && collect.length > max && `編號超過上限 ${max} 組`

/**
 * 判斷是否需要去 fetchProducts
 */
const getShouldFetchProducts = values => {
  const {
    idType,
    id,
    partNo
  } = values

  const hasFilledId = !_.isEmpty(id)
  const hasFilledPartNo = !_.isNil(partNo)
  const idTypeIsProductId = idType === ID_TYPE.PRODUCT.value

  const shouldFetchProducts = (idTypeIsProductId && hasFilledId) || hasFilledPartNo

  return shouldFetchProducts
}

/**
 * 透過 fetchProducts 獲得有包含這些 product 的 listing id
 */
const getListingIdsBy = async (fetchProductsActionCreator, options) => {
  try {
    const productsPayload = await fetchProductsActionCreator(options)
      .then(resHandler)
    const { products } = productsPayload
    // console.log('getListingIdsBy.productsPayload.product.length: ', products ? products.length : '(invalid)')

    const uniqueListingIds = Array.from(
      new Set(
        _.flatten(
          products
            .map(product => product.listingIdList)
            .filter(idList => !_.isEmpty(idList))
        )
      )
    )

    return uniqueListingIds
  } catch (error) {
    console.log('getListingIdsByFetchingProducts 錯誤', error)
    throw error
  }
}

const MAX_REQUEST_SIZE = 200
const MIN_REQUEST_SIZE = 1
export const DEFAULT_REQUEST_SIZE = 50
const DEFAULT_PAGE_NUMBER = 1
/**
 * 透過 fetchListings 取得與查詢條件相符的賣場清單
 */
const getMatchedListingsBy = async (fetchListingsActionCreator, options) => {
  const { limit } = options

  // 確保 limit 在 1 到 200 的範圍內
  const safeLimit = Math.min(Math.max(limit, MIN_REQUEST_SIZE), DEFAULT_REQUEST_SIZE)
  const safeMaxResults = Math.min(Math.max(limit, MIN_REQUEST_SIZE), MAX_REQUEST_SIZE)

  const [error, listingPayload] = await safeAwait(FetchDataUtil.fetchAllPages(fetchListingsActionCreator,
    {
      payload: { ...options, limit: safeLimit, maxResults: safeMaxResults },
      selector: res => _.get(res, 'listings', [])
    })
  )
  if (error) {
    console.log('getMatchedListings 錯誤', error)
    throw error
  }

  const listings = _.get(listingPayload, 'data', [])
  const result = {
    ..._.omit(listingPayload, ['data']),
    listings
  }

  return result
}

const removeInvalidChar = inputValue => {
  return _.replace(inputValue, /[(|)|+]/g, '')
}

const QueryForm = props => {
  const {
    actions: {
      fetchListings,
      fetchProducts
    },
    error,
    invalid,
    submitting,
    page,
    pageSize, // 查詢筆數
    initialize,
    initialized,
    setHasQueried,
    setListings,
    setPagination,
    handleSubmit,
    subStationOptions,
    silentPageContext,
    flags
  } = props
  const [contactWindow, setContactWindow] = useState('')

  const query = (params = {}) => async (formValues, dispatch, formProps) => {
    const { customPage = page, customPageSize } = params
    const pageSize = customPageSize === null ? DEFAULT_REQUEST_SIZE : customPageSize
    const queryingFormValues = getFormValuesForQuerying(formValues)

    const queryingPage = _.isNull(customPage) ? DEFAULT_PAGE_NUMBER : customPage

    const queryOption = {
      ...queryingFormValues,
      page: queryingPage,
      pageSize
    }

    /* disable state cache, must refresh result on click 2nd time
    const cacheUuid = getUuid(queryOption)
    const cache = props.getStateCache(cacheUuid)
    const hasCache = !_.isNull(cache)
    if (hasCache) {
      const [expiringMoment, result] = cache
      const cacheIsExpired = moment().isAfter(expiringMoment)

      if (!cacheIsExpired) {
        await delay(1500)
        const queryResult = {
          queryOption,
          result
        }

        return queryResult
      }
    }
    */

    const {
      idType,
      ...userInputValues
    } = formValues
    const expectUserInput = _.pick(pruneEmpty(userInputValues), QUERY_FORM_KEYS)
    if (_.isEmpty(expectUserInput)) {
      throw new SubmissionError({
        _error: '請輸入至少一個條件'
      })
    }

    const {
      id,
      name,
      partNo,
      subStationId
    } = queryOption

    const reqPayloadBase = {
      limit: pageSize,
      page: queryingPage
    }

    let involvedListingIdsString = ''
    const shouldFetchProducts = getShouldFetchProducts(queryOption)
    try {
      if (shouldFetchProducts) {
        const queryProductsOptions = _.merge(reqPayloadBase, {
          fields: '+listingIdList',
          id,
          partNo
        })

        const listingIds = await getListingIdsBy(fetchProducts, queryProductsOptions)

        if (_.isEmpty(listingIds)) { return { listings: [] } }

        involvedListingIdsString = listingIds.join(',')
      }

      const queryListingsOptions = _.merge(reqPayloadBase, {
        fields: '',
        name,
        subStationId,
        offset: (queryingPage - 1) * pageSize,
        id: shouldFetchProducts ? involvedListingIdsString : id
      })

      const matchedListings = await getMatchedListingsBy(fetchListings, queryListingsOptions)

      /* disable state cache, must refresh result on click 2nd time
      const expiringMoment = moment().add(...CACHE_EXPIRE_TIME)
      props.setStateCache(cacheUuid, [expiringMoment, matchedListings])
      */

      return {
        queryOption,
        result: matchedListings
      }
    } catch (error) {
      console.log('fetchSpecificListing error', error)
    }
  }

  const handleQuery = customPageOption => async () => {
    const triggerQuery = promisifySubmitResult(handleSubmit(query(customPageOption)))

    try {
      const {
        queryOption = {},
        result = {}
      } = await triggerQuery()
      HistoryUtil.updateHistory(queryOption)

      const {
        listings,
        pagination
      } = result
      setListings(listings)
      setPagination(pagination)
      setHasQueried(true)
    } catch (error) {
      console.log('handleQuery error', error)
    }
  }

  useEffect(
    () => {
      if (initialized && !_.isNull(page)) {
        handleQuery({ customPage: page, customPageSize: pageSize })()
      }
    },
    [page, pageSize, initialized]
  )

  useEffect(
    () => {
      const defaultFormValues = { idType: 'listing' }
      const urlFormValues = getUrlFormValues(props)
      initialize(_.merge(defaultFormValues, urlFormValues))
      // 查詢頁不需要跳出`資料尚未儲存`提示
      // silentPageContext 的 provider 設定在 QueryPage 的 CommonPage
      silentPageContext.setSilentPageTo(true)
      const { user } = props
      setContactWindow(getContactWindow(user))
    },
    []
  )

  const kaFeatureToggle = getFeatureToggle(flags, 'sp_KA')
  return (
    <ProposalBlock>
      <form
        key='query-listing-form'
        autoComplete='off'
      >
        {
          kaFeatureToggle && (
            <FormRow
              key='subStationIdTip'
              label='對象'
              contentClassName={styles.contactWindow}
            >
              {contactWindow}
            </FormRow>
          )
        }
        <FormRow
          label={kaFeatureToggle ? '賣場站別' : '賣場站別／對象'}
        >
          <Field
            name='subStationId'
            component={RFSelectInputField}
            className={styles.subStationIdSelect}
            options={[
              {
                label: '請選擇',
                value: null
              },
              ...subStationOptions
            ]}
            simpleValue
          />
        </FormRow>
        <FormRow
          label={(
            <Field
              name='idType'
              component={RFSelectInputField}
              className={styles.idTypeSelect}
              options={[
                ID_TYPE.LISTING,
                ID_TYPE.PRODUCT
              ]}
            />
          )}
          contentClassName={styles.fullWithTextarea}
        >
          <Field
            name='id'
            validate={idMax(200)}
            placeholder='請輸入賣場或商品編號，以,分隔，最多 200 組'
            rows={4}
            parse={idsParser}
            format={idsFormatter}
            component={TextareaField}
          />
        </FormRow>
        <FormRow
          label='賣場名稱'
          contentClassName={styles.fullWithInput}
        >
          <Field
            name='name'
            type='text'
            countLimit={100}
            validate={lengthMax(100)}
            placeholder='請輸入關鍵字'
            component={InputCountField}
            parse={removeInvalidChar}
          />
        </FormRow>
        <FormRow
          label='供應商商品料號'
          contentClassName={styles.fullWithInput}
        >
          <Field
            name='partNo'
            type='text'
            countLimit={50}
            validate={lengthMax(50)}
            placeholder='請輸入關鍵字'
            component={InputCountField}
          />
        </FormRow>
        <FormRow>
          <Button
            size='large'
            data-role='query-listing-button'
            onClick={() => {
              props.handlePageSizeChange(DEFAULT_REQUEST_SIZE)
              handleQuery({ customPage: DEFAULT_PAGE_NUMBER, customPageSize: DEFAULT_REQUEST_SIZE })()
            }}
            disabled={submitting || invalid}
          >
            查詢賣場
          </Button>
          <ul className={styles.info}>
            <li> • 永久停售之賣場將自動隱藏</li>
            <li> • 賣場成立後最長需等待15分鐘方能查詢</li>
          </ul>
        </FormRow>
      </form>
      {
        <FormError
          showFormError={!!error}
          error={error}
        />
      }
    </ProposalBlock>
  )
}

export default withSilentPageContext(QueryForm)
